import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { showErrorToast, showSuccessToast } from "@/lib/toast-utils";

interface AppSetting {
  id: string;
  setting_key: string;
  setting_value: string;
  description: string | null;
}

export const useAppSettings = () => {
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);

  const fetchSettings = async () => {
    try {
      const { data, error } = await (supabase as any)
        .from('app_settings')
        .select('*');

      if (error) throw error;

      const settingsMap: Record<string, string> = {};
      data?.forEach((setting: AppSetting) => {
        settingsMap[setting.setting_key] = setting.setting_value;
      });
      setSettings(settingsMap);
    } catch (error) {
      console.error('Error fetching app settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateSetting = async (key: string, value: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('update-app-setting', {
        body: { key, value }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setSettings(prev => ({ ...prev, [key]: value }));
      showSuccessToast(`${key} has been updated`, 'Setting updated');
    } catch (error) {
      console.error('Error updating setting:', error);
      showErrorToast('Failed to update setting');
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  return { settings, isLoading, updateSetting, refetch: fetchSettings };
};

// Helper to get API hostname
export const getApiHostname = async (): Promise<string> => {
  const { data, error } = await (supabase as any)
    .from('app_settings')
    .select('setting_value')
    .eq('setting_key', 'api_hostname')
    .single();

  if (error || !data) {
    return 'https://staging-jayco.mydwsite.com';
  }
  return (data as any).setting_value;
};
